import { request as umiRequest } from 'umi';
import { message } from 'antd';
import { setAuth, getAuth } from "./db";
import { Toast } from "antd-mobile";
import { redirectToLogin } from '@/utils/authGuard';

export const post = { method: 'POST' };
export const lang = (navigator.language === 'en' ? 'en-US' : navigator.language) || 'en-US';

// 请求计数器
let requestCount = 0;
// 存储原始路径，避免重复跳转后丢失
let originalRedirectPath = '';
// 防止重复跳转
let isRedirecting = false;
// Loading 实例引用
let loadingInstance = null;

/**
 * 显示 loading Toast
 */
const showLoadingToast = () => {
    if (!loadingInstance) {
        loadingInstance = Toast.show({
            content: 'loading...',
            icon: 'loading',
            duration: 0,
        });
    }
};

/**
 * 隐藏 loading Toast
 */
const hideLoadingToast = () => {
    if (loadingInstance) {
        loadingInstance.close();
        loadingInstance = null;
    }
};

/**
 * 显示普通消息 Toast
 */
const showMessageToast = (content, duration = 2000) => {
    if (!content) return;

    // 延迟显示，避免被 loading 关闭影响
    setTimeout(() => {
        Toast.show({
            content,
            duration,
        });
    }, 50);
};

/**
 * http 异常状态码对应的提示信息
 */
const HttpErrors = {
    401: '登录已过期，请重新登录',
    403: '无权访问',
    404: '地址不存在',
    405: '非法的接口请求类型',
    409: '账号已在其他设备登录',
    413: '文件过大，请更换文件后重试',
    502: '系统不可用，请稍后重试',
    503: '系统维护升级中，请稍后再进行访问，给您带来不便敬请谅解',
    504: '网关超时',
};

// 需要跳转登录页的状态码
const LOGIN_REDIRECT_CODES = [401, 409];

/**
 * 重置重定向路径（登录成功后调用）
 */
export const resetRedirectPath = () => {
    originalRedirectPath = '';
};

/**
 * 处理登录跳转（仅用于未授权/过期情况）
 */
const handleLoginRedirect = (status, msg) => {
    if (isRedirecting) return;

    isRedirecting = true;
    if (msg) showMessageToast(msg || HttpErrors[status]);

    setTimeout(() => {
        isRedirecting = false;
        redirectToLogin();
    }, 500);
};

/**
 * 显示 loading（请求拦截器中使用）
 */
const showLoading = () => {
    if (requestCount === 0) {
        showLoadingToast();
    }
    requestCount++;
};

/**
 * 隐藏 loading（响应拦截器中使用）
 */
const hideLoading = () => {
    requestCount--;
    if (requestCount <= 0) {
        hideLoadingToast();
        requestCount = 0;
    }
};

/**
 * 判断是否需要跳过错误提示（主要用于文件流下载）
 */
const skipErrorMsg = ({ status, request, data, config }) => {
    if (status === 200 && (request?.responseType === 'blob' || config?.skipErrorHandler)) {
        return true;
    }
    return false;
};

const local = localStorage.getItem('umi_locale') || lang;

/**
 * UMI 请求配置
 */
export const requestConfig = {
    timeout: 60 * 1000,
    timeoutErrorMessage: '网络不稳定，连接超时了',
    headers: { lang: local },
    errorConfig: {
        // 错误抛出
        errorThrower: (res) => {
            const { success, status } = res;
            const msg = res.data?.msg;
            if (!success) {
                const error = new Error(msg);
                error.name = 'customError';
                error.info = { status, message: msg };
                throw error;
            }
        },
        // 错误接收及处理
        errorHandler: (error, opts) => {
            const { response, name, info, request } = error || {};
            if (opts?.skipErrorHandler && response?.status === 200) throw error;

            message.destroy('imageLoading');

            if (name === 'customError') {
                const msg = info?.data?.msg;
                if (msg) showMessageToast(msg);
            } else if (response) {
                if (LOGIN_REDIRECT_CODES.includes(response.status)) {
                    handleLoginRedirect(response.status, HttpErrors[response.status]);
                } else {
                    showMessageToast(HttpErrors[response.status] || '网络不稳定，请稍后再试');
                }
            } else if (error?.message?.includes('timeout')) {
                showMessageToast('网络不稳定，连接超时了');
            }
        }
    },
    // 请求拦截器
    requestInterceptors: [
        (url, options) => {
            showLoading();
            return {
                url,
                options: {
                    ...options,
                    headers: {
                        ...options?.headers,
                        token: getAuth(),
                    },
                },
            };
        },
    ],
    // 响应拦截器
    responseInterceptors: [
        (response) => {
            hideLoading();
            const { data, headers } = response;
            const auth = data?.data?.token;

            // 处理需要跳转登录的状态码
            if (LOGIN_REDIRECT_CODES.includes(data?.status)) {
                handleLoginRedirect(data.status, data.msg);
            }

            // 保存 token
            if (auth) {
                setAuth(auth);
            }

            data.success = skipErrorMsg(response);
            return {
                ...response,
                data,
            };
        },
    ],
};

/**
 * 封装的请求方法
 * @param {string} url - 请求地址
 * @param {object} options - 请求配置
 * @returns {Promise} 返回响应数据
 */
export const request = async (url, options) => {
    let res;
    try {
        res = await umiRequest(url, options);

        // 处理需要跳转登录的状态码（注意：排除正常的 200 和业务成功状态）
        if (res?.status && LOGIN_REDIRECT_CODES.includes(res.status)) {
            handleLoginRedirect(res.status, res.msg);
            return res;
        }

        // 处理业务错误（status 不为 1 时显示错误提示）
        // 注意：登录成功的接口通常返回 status === 1 或 data 中包含 token
        if (res && res.status !== 1 && res.msg && !options?.silentBizMsg) {
            showMessageToast(res.msg);
        }
    } catch (e) {
        console.error('请求错误:', e);

        // 处理 HTTP 错误
        if (e.response) {
            if (LOGIN_REDIRECT_CODES.includes(e.response.status)) {
                handleLoginRedirect(e.response.status, HttpErrors[e.response.status]);
            } else {
                showMessageToast(HttpErrors[e.response.status] || '请求失败');
            }
        } else if (e.request) {
            showMessageToast('网络错误，请检查网络连接');
        } else if (e.message) {
            showMessageToast(e.message || '未知错误');
        }

        res = e?.response?.data;
    }

    return res;
};

/**
 * 通用的 API 调用方法
 * @param {object} params - 参数对象
 * @param {function} params.api - API 方法
 * @param {object} params.params - 请求参数
 * @param {function} params.success - 成功回调
 * @returns {any} 返回响应数据
 */
export const getApi = async ({ api, params, success }) => {
    const response = await api(params);

    // 处理需要跳转登录的状态码
    if (response?.status && LOGIN_REDIRECT_CODES.includes(response.status)) {
        handleLoginRedirect(response.status, response.msg);
        return null;
    }

    // 业务成功（status 为 1 或 0）
    if (response?.status === 1) {
        success?.();
        return response.data;
    }

    // 业务失败
    if (response?.msg) {
        showMessageToast(response.msg);
    }

    return null;
};

/**
 * 轮询指定接口直到满足条件或超时
 * @param {Function} apiFn - 调用接口的函数，需返回 Promise
 * @param {Function} checkFn - 判断结果是否满足条件的函数
 * @param {Object} options - 配置选项
 * @param {number} [options.interval=1000] - 轮询间隔（毫秒）
 * @param {number} [options.timeout=30000] - 超时时间（毫秒）
 * @param {Function} [options.onError] - 错误处理函数
 * @returns {Promise} - 返回一个 Promise，resolve 成功结果，reject 超时或错误
 */
export const pollApi = async (apiFn, checkFn, options = {}) => {
    const {
        interval = 1000,
        timeout = 30000,
        onError = (err) => console.error('Polling error:', err),
    } = options;

    const startTime = Date.now();

    while (Date.now() - startTime < timeout) {
        try {
            const result = await apiFn();

            // 处理需要跳转登录的状态码
            if (result?.status && LOGIN_REDIRECT_CODES.includes(result.status)) {
                handleLoginRedirect(result.status, result.msg);
                throw new Error('登录状态异常，停止轮询');
            }

            // 检查是否满足条件
            if (checkFn(result)) {
                return result;
            }
        } catch (err) {
            onError(err);
            // 如果是登录异常，停止轮询
            if (err.message === '登录状态异常，停止轮询') {
                throw err;
            }
        }

        // 等待下一次轮询
        await new Promise((resolve) => setTimeout(resolve, interval));
    }

    throw new Error('轮询超时');
};

/**
 * 导出清除 loading 的方法（供特殊情况使用）
 */
export const clearLoading = () => {
    requestCount = 0;
    hideLoadingToast();
};